import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Send, CheckCircle, XCircle, Calendar, Users, Edit, Clock, ShieldCheck } from "lucide-react";
import { format } from "date-fns";
import { StatusBadge, getStatusDisplay, normalizeStatus } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import { getDepartmentColorsMap, getDepartmentDisplay } from "@/lib/department-utils";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatFramework } from "@/hooks/use-compliance-programs";

interface Policy {
  id: string;
  title: string;
  department: string | null;
  description: string | null;
  version: number;
  status: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  created_by: string;
  created_at: string;
  updated_at: string;
  effective_date: string | null;
  next_renewal_date: string | null;
  assigned_reviewers?: string[] | null;
  assigned_approvers?: string[] | null;
  creator: {
    name: string;
  } | null;
  program_id?: string | null;
  program?: {
    id: string;
    name: string;
    framework: string;
  } | null;
}

const STATUS_CONFIG = {
  draft: {
    accent: 'border-slate-300 dark:border-slate-600',
    pill: 'bg-slate-400/10 text-slate-500 dark:text-slate-400',
    dot: 'bg-slate-400',
  },
  under_review: {
    accent: 'border-amber-300 dark:border-amber-600',
    pill: 'bg-amber-400/10 text-amber-600 dark:text-amber-400',
    dot: 'bg-amber-400',
  },
  pending_approval: {
    accent: 'border-blue-300 dark:border-blue-600',
    pill: 'bg-blue-400/10 text-blue-600 dark:text-blue-400',
    dot: 'bg-blue-400',
  },
  approved: {
    accent: 'border-emerald-300 dark:border-emerald-600',
    pill: 'bg-emerald-400/10 text-emerald-600 dark:text-emerald-400',
    dot: 'bg-emerald-400',
  },
  rejected: {
    accent: 'border-red-300 dark:border-red-600',
    pill: 'bg-red-400/10 text-red-600 dark:text-red-400',
    dot: 'bg-red-400',
  },
} as const;

interface ResponsivePolicyCardProps {
  policy: Policy;
  showActionButton?: boolean;
  actionButtonText?: string;
  actionButtonIcon?: React.ComponentType<{ className?: string }>;
  onActionClick?: () => void;
  actionButtonVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
}

export function ResponsivePolicyCard({
  policy,
  showActionButton = false,
  actionButtonText = "Action",
  actionButtonIcon: ActionIcon = Send,
  onActionClick,
  actionButtonVariant = "default",
  className
}: ResponsivePolicyCardProps) {
  const { profile } = useAuth();
  const [departmentColors, setDepartmentColors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (profile?.company_id) {
      getDepartmentColorsMap(profile.company_id)
        .then(setDepartmentColors)
        .catch(console.error);
    }
  }, [profile?.company_id]);

  const departmentColor = policy.department ? departmentColors[policy.department] : null;
  const statusConfig = STATUS_CONFIG[policy.status] ?? STATUS_CONFIG.draft;

  return (
    <div className={cn(
      "rounded-2xl bg-card flex flex-col h-full overflow-hidden border transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-0.5 hover:shadow-md",
      className
    )}>
        <Link to={`/policy/${policy.id}`} className="flex-1 flex flex-col p-5 pb-4 gap-3 min-h-0">
          <div className="flex items-center justify-between gap-2">
            <StatusBadge status={normalizeStatus(policy.status) as any} className="rounded-full text-[10px] uppercase tracking-[0.1em]">
              {getStatusDisplay(normalizeStatus(policy.status))}
            </StatusBadge>
            <span className="text-[11px] text-muted-foreground font-mono">v{policy.version}</span>
          </div>

          <h3 className="text-xl font-semibold leading-tight line-clamp-2 text-foreground">
            {policy.title}
          </h3>

          <div>
            <Badge
              variant="secondary"
              className="text-[11px] font-medium rounded-full px-2.5 py-0.5"
              style={departmentColor ? {
                backgroundColor: departmentColor + '18',
                color: departmentColor,
                border: '1px solid ' + departmentColor + '35',
              } : undefined}
            >
              {getDepartmentDisplay(policy.department)}
            </Badge>
            {policy.program && (
              <Badge variant="outline" className="ml-2 text-[11px] font-medium rounded-full px-2.5 py-0.5">
                <ShieldCheck className="mr-1 h-3 w-3" />
                {formatFramework(policy.program.framework)}
              </Badge>
            )}
          </div>

          {policy.description && (
            <p className="text-[12px] text-muted-foreground line-clamp-2">{policy.description}</p>
          )}

          <div className="mt-auto pt-1 space-y-1.5">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Calendar className="w-3 h-3 opacity-60 flex-shrink-0" />
              <span>Updated {format(new Date(policy.updated_at), "MMM d, yyyy")}</span>
            </div>
            {policy.creator?.name && (
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Users className="w-3 h-3 opacity-60 flex-shrink-0" />
                <span className="truncate">{policy.creator.name}</span>
              </div>
            )}
          </div>
        </Link>

        {showActionButton && onActionClick && (
          <div className="px-5 pb-5 pt-0" onClick={(e) => e.stopPropagation()}>
            <div className="h-[1px] bg-white/[0.05] mb-4" />
            <Button
              size="sm"
              variant={actionButtonVariant}
              onClick={onActionClick}
              className="w-full rounded-full h-9 text-sm font-medium"
            >
              <ActionIcon className="w-3.5 h-3.5 mr-1.5" />
              {actionButtonText}
            </Button>
          </div>
        )}
    </div>
  );
}

interface PolicyLibraryCardProps {
  policy: Policy;
  className?: string;
  userRoles?: string[];
  onEditDraft?: (policyId: string) => void;
  onSendForApproval?: (policy: Policy) => void;
  onApprove?: (policy: Policy) => void;
  onReject?: (policy: Policy) => void;
}

export function PolicyLibraryCard({
  policy,
  className,
  userRoles = [],
  onEditDraft,
  onSendForApproval,
  onApprove,
  onReject
}: PolicyLibraryCardProps) {
  const { profile } = useAuth();
  const [departmentColors, setDepartmentColors] = useState<Record<string, string>>({});
  const [reviewerNames, setReviewerNames] = useState<string[]>([]);
  const [approverNames, setApproverNames] = useState<string[]>([]);
  const [loadingNames, setLoadingNames] = useState(true);

  useEffect(() => {
    if (profile?.company_id) {
      getDepartmentColorsMap(profile.company_id)
        .then(setDepartmentColors)
        .catch(console.error);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    const fetchNames = async () => {
      if (policy.status !== 'under_review' && policy.status !== 'pending_approval') {
        setLoadingNames(false);
        return;
      }

      try {
        const userIds = policy.status === 'under_review'
          ? policy.assigned_reviewers || []
          : policy.assigned_approvers || [];

        if (userIds.length === 0) {
          setLoadingNames(false);
          return;
        }

        const { data } = await supabase
          .from('profiles')
          .select('name')
          .in('id', userIds);

        const names = data?.map(p => p.name) || [];
        if (policy.status === 'under_review') {
          setReviewerNames(names);
        } else {
          setApproverNames(names);
        }
      } catch (error) {
        console.error('Error fetching names:', error);
      } finally {
        setLoadingNames(false);
      }
    };

    fetchNames();
  }, [policy.status, policy.assigned_reviewers, policy.assigned_approvers]);

  const departmentColor = policy.department ? departmentColors[policy.department] : null;
  const statusConfig = STATUS_CONFIG[policy.status] ?? STATUS_CONFIG.draft;

  const getMetaRows = () => {
    const rows: { icon: React.ComponentType<{ className?: string }>; label: string }[] = [];

    switch (policy.status) {
      case 'draft':
        if (policy.creator?.name) rows.push({ icon: Users, label: policy.creator.name });
        if (policy.effective_date) rows.push({ icon: Calendar, label: `Effective ${format(new Date(policy.effective_date), "MMM d, yyyy")}` });
        break;

      case 'under_review':
        if (loadingNames) {
          rows.push({ icon: Clock, label: 'Loading reviewers…' });
        } else {
          const names = reviewerNames.length > 0 ? reviewerNames.join(', ') : 'No reviewers assigned';
          rows.push({ icon: Users, label: `With ${names}` });
        }
        if (policy.effective_date) rows.push({ icon: Calendar, label: `Effective ${format(new Date(policy.effective_date), "MMM d, yyyy")}` });
        break;

      case 'pending_approval':
        if (loadingNames) {
          rows.push({ icon: Clock, label: 'Loading approvers…' });
        } else {
          const names = approverNames.length > 0 ? approverNames.join(', ') : 'No approvers assigned';
          rows.push({ icon: Users, label: `Awaiting ${names}` });
        }
        if (policy.effective_date) rows.push({ icon: Calendar, label: `Effective ${format(new Date(policy.effective_date), "MMM d, yyyy")}` });
        break;

      case 'approved':
        if (policy.effective_date) rows.push({ icon: Calendar, label: `Effective ${format(new Date(policy.effective_date), "MMM d, yyyy")}` });
        if (policy.next_renewal_date) rows.push({ icon: Calendar, label: `Renews ${format(new Date(policy.next_renewal_date), "MMM d, yyyy")}` });
        if (!policy.effective_date && policy.creator?.name) rows.push({ icon: Users, label: policy.creator.name });
        break;

      case 'rejected':
        if (policy.creator?.name) rows.push({ icon: Users, label: policy.creator.name });
        break;
    }

    return rows.slice(0, 2);
  };

  const metaRows = getMetaRows();

  const canSendForApproval = policy.status === 'under_review' && !!onSendForApproval && userRoles.some(r => ['admin', 'reviewer'].includes(r));
  const canApproveReject = policy.status === 'pending_approval' && !!onApprove && !!onReject && userRoles.some(r => ['admin', 'approver'].includes(r));
  const hasActions = policy.status === 'draft' || canSendForApproval || canApproveReject;

  return (
    <div className={cn(
      "rounded-2xl bg-card flex flex-col h-full overflow-hidden border transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-0.5 hover:shadow-md",
      statusConfig.accent,
      className
    )}>
        <Link to={`/policy/${policy.id}`} className="flex-1 flex flex-col p-5 gap-3 min-h-0">
          {/* Status pill + version */}
          <div className="flex items-center justify-between gap-2">
            <span className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.12em] font-medium",
              statusConfig.pill
            )}>
              <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", statusConfig.dot)} />
              {getStatusDisplay(normalizeStatus(policy.status))}
            </span>
            <span className="text-[11px] text-muted-foreground font-mono flex-shrink-0">v{policy.version}</span>
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold leading-tight line-clamp-2 text-foreground">
            {policy.title}
          </h3>

          {/* Department */}
          <div>
            <Badge
              variant="secondary"
              className="text-[11px] font-medium rounded-full px-2.5 py-0.5"
              style={departmentColor ? {
                backgroundColor: departmentColor + '18',
                color: departmentColor,
                border: '1px solid ' + departmentColor + '35',
              } : undefined}
            >
              {getDepartmentDisplay(policy.department)}
            </Badge>
            {policy.program && (
              <Badge variant="outline" className="ml-2 text-[11px] font-medium rounded-full px-2.5 py-0.5">
                <ShieldCheck className="mr-1 h-3 w-3" />
                {formatFramework(policy.program.framework)}
              </Badge>
            )}
          </div>

          {/* Contextual metadata */}
          {metaRows.length > 0 && (
            <div className="mt-auto space-y-1.5 pt-1">
              {metaRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <row.icon className="w-3 h-3 flex-shrink-0 opacity-60" />
                  <span className="truncate">{row.label}</span>
                </div>
              ))}
            </div>
          )}
        </Link>

        {/* Actions */}
        {hasActions && (
          <div className="px-5 pb-5 pt-0" onClick={(e) => e.stopPropagation()}>
            <div className="h-[1px] bg-white/[0.05] mb-4" />

            {policy.status === 'draft' && (
              onEditDraft ? (
                <Button
                  className="w-full rounded-full h-9 text-sm font-medium"
                  onClick={() => onEditDraft(policy.id)}
                >
                  <Edit className="w-3.5 h-3.5 mr-1.5" />
                  Edit Draft
                </Button>
              ) : (
                <Link to={`/policy-creator?edit=${policy.id}`} className="block">
                  <Button className="w-full rounded-full h-9 text-sm font-medium">
                    <Edit className="w-3.5 h-3.5 mr-1.5" />
                    Edit Draft
                  </Button>
                </Link>
              )
            )}

            {canSendForApproval && (
              <Button
                className="w-full rounded-full h-9 text-sm font-medium"
                onClick={() => onSendForApproval!(policy)}
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Send for Approval
              </Button>
            )}

            {canApproveReject && (
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  className="flex-1 rounded-full h-9 text-sm font-medium"
                  onClick={() => onReject!(policy)}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" />
                  Reject
                </Button>
                <Button
                  className="flex-1 rounded-full h-9 text-sm font-medium"
                  onClick={() => onApprove!(policy)}
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                  Approve
                </Button>
              </div>
            )}
          </div>
        )}
    </div>
  );
}
